/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:38:39                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 16:24:06                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * TechnicianVanSelect: customer select component
    * useEffect: Transform value to be printed in select
    * getTechnicianVanList: Get customers list from api
    * customerChange: handle customer change event
    * handleTechnicianVanCreate: handle customer creation on fly
*/

/* Imports */
import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Input } from "@material-tailwind/react";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
import { showApiErrorToast } from "../../../services/api/error";
/***/

/* Components */
import toast from "react-hot-toast";
/***/

/**
* TechnicianVanSelect: customer select component
* Used to select a customer entity
* @param value - current value
* @param onChange - on value change event
* @param technician - technician object to check if has van
* @param onVanCreated - callback when van is created
* @param disabled - enabled/disabled state
* @param isClearable - allow field clear action
* @param isCreatable - allow on fly creation
* @param isEditable - allow on fly edition
* @param isMulti - allow multiple selection
* @param className - additional classnames
*/
const TechnicianVanSelect = ({
  value,
  onChange,
  technician,
  onVanCreated,
  disabled,
  className = ""
}: {
  value?: Partial<EasyTech.TechnicianVan>,
  onChange?: (value: any) => void,
  technician?: Partial<EasyTech.Technician>,
  onVanCreated?: (van: EasyTech.TechnicianVan) => void,
  disabled?: boolean,
  className?: string,
}) => {
  const { t } = useTranslation();
  const [valueState, setValueState] = useState<any>(null);
  const [timestamp, setTimestamp] = useState<number>(Date.now());
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [vanName, setVanName] = useState<string>("");
  const [vanOdooId, setVanOdooId] = useState<string>("");
  const [isCreating, setIsCreating] = useState<boolean>(false);

  /**
  * useEffect: Transform value to be printed in select 
  * @param value - reload on value change
  */
  useEffect(() => {
    if (value && (value as EasyTech.TechnicianVan).id) {
      setValueState({value: value, label: (value as EasyTech.TechnicianVan).name});
    }
    else setValueState(null);
    setTimestamp(Date.now()); // Force reload customers list
  }, [value]);
  /***/

  /**
    * customerChange: handle customer change event 
    * @param value - new value
    */
  const vanChange = (value: any) => {
    const ret = (value ? value.value : value);
    setValueState(value);
    if (onChange) onChange(ret);
  };
  /***/

  const getVanList = ():Promise<EasyTech.IFormData<EasyTech.TechnicianVan>[]> => {
    return new Promise((resolve) => {
      api.get("admin/technician_van")
        .then(async (response: any) => {
          const ret = response as EasyTech.TechnicianVan[];
          return resolve(
            setSelectList<EasyTech.TechnicianVan>(ret, (el) => el.name)
          );
        }).catch((err) => {
          console.error(err);
        });
    });
  };

  /**
   * Créer un nouveau van et l'assigner au technicien
   */
  const handleCreateVan = async () => {
    if (!vanName.trim() || !technician?.id) {
      toast.error(t("common:requiredField"));
      return;
    }

    try {
      setIsCreating(true);
      const payload = {
        odooId: vanOdooId || `VAN-${Date.now()}`,
        name: vanName,
        technician: {
          id: technician.id
        }
      };

      const response = await api.post("admin/technician_van", payload);
      const newVan = response as unknown as EasyTech.TechnicianVan;

      toast.success(t("common:messages.created"));
      
      // Callback
      if (onVanCreated) {
        onVanCreated(newVan);
      }

      // Update select
      if (onChange) {
        onChange(newVan);
      }

      setShowCreateDialog(false);
      setVanName("");
      setVanOdooId("");
      setTimestamp(Date.now()); // Force reload list
    } catch (error) {
      showApiErrorToast(error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={`flex w-full flex-col gap-2 ${className}`}>
      <div className="flex gap-2">
        <AsyncSelect  key={timestamp}
                      cacheOptions
                      defaultOptions
                      loadOptions={getVanList}
                      value={valueState}
                      theme={(theme) => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary: "rgb(14 165 233)",
                        },
                      })}
                      onChange={vanChange}
                      className="w-full"
                      name="customer"
                      placeholder={t("common:selectTechnicianVan")}
                      classNamePrefix="react-select"
                      isClearable={true}
                      isDisabled={disabled} />
        
        {/* Show create button if technician has no van */}
        {technician?.id && !technician?.van && !disabled && (
          <Button
            onClick={() => setShowCreateDialog(true)}
            variant="filled"
            color="blue"
            className="normal-case font-thin flex-shrink-0"
            size="sm"
          >
            {t("common:actions.create")}
          </Button>
        )}
      </div>

      {/* Create Van Dialog */}
      <Dialog
        open={showCreateDialog}
        handler={() => setShowCreateDialog(false)}
        className="p-6"
      >
        <div className="space-y-4">
          <h2 className="text-xl font-bold">{t("common:createVan")}</h2>
          
          <Input
            label={t("common:van.odooId")}
            placeholder={`VAN-${Date.now()}`}
            value={vanOdooId}
            onChange={(e) => setVanOdooId(e.target.value)}
          />
          
          <Input
            label={t("common:van.name") + " *"}
            placeholder={t("common:van.name")}
            value={vanName}
            onChange={(e) => setVanName(e.target.value)}
          />

          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => setShowCreateDialog(false)}
              variant="text"
              color="gray"
              className="normal-case"
            >
              {t("common:actions.cancel")}
            </Button>
            <Button
              onClick={handleCreateVan}
              variant="filled"
              color="blue"
              className="normal-case"
              disabled={isCreating || !vanName.trim()}
              loading={isCreating}
            >
              {t("common:actions.create")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TechnicianVanSelect;
